import { useMemo, useState } from 'react'
import { Lock, X } from 'lucide-react'
import { toast } from 'sonner'
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogTitle, DpeGradeBadge, Label, Logo } from '@alerte-immo/ui'
import { TIERS, formatTierPrice } from '@alerte-immo/plans'
import { ThemeToggle } from '@/components/theme/ThemeToggle'
import { price, pricePerSquareMeter } from '@/lib/formatters'
import type { OnboardingAnswers } from './api'

/**
 * Maquette publique du brief d'essai. Aucun appel API, aucun checkout.
 * Les chiffres sont illustratifs. La commune est celle qu'aurait déjà
 * l'inscription.
 */

type Persona = 'agent' | 'investisseur' | 'marchand' | 'autre'
type PropertyType = 'maison' | 'appartement' | 'immeuble' | 'terrain'
type Pace = 'matin' | 'lundi' | 'bihebdo' | 'prioritaire' | 'autre'
type Step = 'intro' | 0 | 1 | 2 | 3 | 'result'

const COMMUNE = 'La Roche-sur-Yon'
const DETECTED = 47
const VISIBLE = 3
const TRIAL_DAYS = 15
const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre']

const PERSONAS: { id: Persona; label: string }[] = [
  { id: 'agent', label: 'Agent immobilier' },
  { id: 'investisseur', label: 'Investisseur' },
  { id: 'marchand', label: 'Marchand de biens' },
  { id: 'autre', label: 'Autre' },
]

const PROPERTY_TYPES: { id: PropertyType; label: string }[] = [
  { id: 'maison', label: 'Maison' },
  { id: 'appartement', label: 'Appartement' },
  { id: 'immeuble', label: 'Immeuble' },
  { id: 'terrain', label: 'Terrain' },
]


const PACE: { id: Pace; label: string }[] = [
  { id: 'matin', label: 'Chaque matin' },
  { id: 'lundi', label: 'Le lundi seulement' },
  { id: 'bihebdo', label: 'Deux fois par semaine' },
  { id: 'prioritaire', label: 'Seulement si un bien prioritaire sort' },
  { id: 'autre', label: 'Autre' },
]

const ROLE: Record<Exclude<Persona, 'autre'>, string> = {
  agent: 'agent immobilier',
  investisseur: 'investisseur',
  marchand: 'marchand de biens',
}

const MECHANISM: Record<Exclude<Persona, 'autre'>, string> = {
  agent: 'Les adresses sortent avant l’annonce. La pige commence trop tard.',
  investisseur: 'Le prix estimé et le DPE sont déjà là, avant que l’annonce crée la file.',
  marchand: 'DPE récent, logement vacant, aucune annonce : la marge se joue là.',
}


const VISIBLE_ROWS = [
  { address: '14 rue du Port', meta: '112 m²', price: 214_000, grade: 'E' as const, pct: 61, when: '24 sept.' },
  { address: '8 avenue Clemenceau', meta: '74 m²', price: 167_500, grade: 'D' as const, pct: 48, when: '23 sept.' },
  { address: '3 impasse des Lilas', meta: '128 m²', price: 241_000, grade: 'F' as const, pct: 72, when: '22 sept.' },
]

const GIFT = {
  address: '12 rue des Acacias',
  meta: `${COMMUNE} · 85000 · Maison · 98 m²`,
  price: 186_000,
  surface: 98,
  pct: 54,
  energy: 'E' as const,
  ges: 'D' as const,
}

const QUESTIONS = [
  {
    title: 'Dans l’immobilier, vous êtes…',
    help: 'Plusieurs réponses possibles.',
    otherPlaceholder: 'Mandataire, chasseur, notaire…',
    options: PERSONAS,
  },
  {
    title: 'Quel type de bien cherchez-vous ?',
    help: 'Plusieurs réponses possibles.',
    otherPlaceholder: '',
    options: PROPERTY_TYPES,
  },
  {
    title: 'Quand voulez-vous être prévenu ?',
    help: 'Les alertes partent à 8 h.',
    otherPlaceholder: 'Précisez le rythme',
    options: PACE,
  },
] as const

function addDays(base: Date, days: number) {
  const next = new Date(base)
  next.setHours(12, 0, 0, 0)
  next.setDate(next.getDate() + days)
  return next
}

function dayMonth(d: Date) {
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`
}

function mondayPhrase(mondays: Date[]) {
  const [first, second] = mondays
  if (first && second && first.getMonth() === second.getMonth()) {
    return `le ${first.getDate()} et le ${second.getDate()} ${MONTHS[first.getMonth()]}`
  }
  if (first && second) return `le ${dayMonth(first)} et le ${dayMonth(second)}`
  if (first) return `le ${dayMonth(first)}`
  return 'les deux prochains lundis'
}

function trialWindow(from = new Date()) {
  const chargeOn = addDays(from, TRIAL_DAYS)
  const mondays: Date[] = []
  const cursor = addDays(from, 1)
  while (cursor <= chargeOn && mondays.length < 2) {
    if (cursor.getDay() === 1) mondays.push(new Date(cursor))
    cursor.setDate(cursor.getDate() + 1)
  }
  return { chargeOn, mondays }
}

function joinFr(items: string[]) {
  if (items.length <= 1) return items[0] ?? ''
  return `${items.slice(0, -1).join(', ')} et ${items[items.length - 1]}`
}

function whoLine(personas: Persona[], other: string) {
  const parts = personas.filter((p): p is Exclude<Persona, 'autre'> => p !== 'autre').map((p) => ROLE[p])
  if (personas.includes('autre') && other.trim()) parts.push(other.trim())
  if (parts.length === 0) return 'Vous cherchez des biens avant leur mise en vente.'
  return `Vous êtes ${joinFr(parts)}.`
}

function alertLabel(pace: Pace | null, other: string, mondays: string) {
  if (pace === 'matin') return 'Chaque matin, à 8 h'
  if (pace === 'lundi') return 'Le lundi, à 8 h'
  if (pace === 'bihebdo') return 'Deux fois par semaine, à 8 h'
  if (pace === 'prioritaire') return 'Seulement si un bien prioritaire sort'
  if (pace === 'autre') return other.trim() ? other.trim() : 'Selon votre rythme'
  return `${mondays}, à 8 h`
}

export function TrialBriefPage({
  embedded = false,
  open,
  onOpenChange,
  onAnswered,
}: {
  embedded?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onAnswered?: (answers: OnboardingAnswers) => Promise<void>
} = {}) {
  const trial = useMemo(() => trialWindow(), [])
  const [step, setStep] = useState<Step>('intro')
  const [saving, setSaving] = useState(false)
  const [internalOpen, setInternalOpen] = useState(true)
  const briefOpen = embedded ? Boolean(open) : internalOpen
  function setBriefOpen(next: boolean) {
    if (embedded) onOpenChange?.(next)
    else setInternalOpen(next)
  }
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [ficheOpen, setFicheOpen] = useState(false)
  const [personas, setPersonas] = useState<Persona[]>([])
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([])
  const [pace, setPace] = useState<Pace | null>(null)
  const [paceOther, setPaceOther] = useState('')
  const [personaOther, setPersonaOther] = useState('')
  const [missing, setMissing] = useState('')
  const tierMeta = TIERS.prospection
  const maskedLeft = ficheOpen ? DETECTED - 1 : DETECTED
  const mondays = mondayPhrase(trial.mondays)
  const charge = dayMonth(trial.chargeOn)
  const alerts = alertLabel(pace, paceOther, mondays)
  const lead = personas.find((item) => item !== 'autre')
  const sought = joinFr(propertyTypes.map((type) => PROPERTY_TYPES.find((item) => item.id === type)?.label.toLowerCase() ?? type))

  function restart() {
    setPersonas([])
    setPropertyTypes([])
    setPace(null)
    setPaceOther('')
    setPersonaOther('')
    setMissing('')
    setStep('intro')
    setFicheOpen(false)
    setConfirmOpen(false)
    setBriefOpen(true)
  }

  function reveal() {
    setBriefOpen(false)
    setFicheOpen(true)
  }

  function toggle<T extends string>(list: T[], id: T, set: (next: T[]) => void) {
    set(list.includes(id) ? list.filter((item) => item !== id) : [...list, id])
  }

  async function continueBrief() {
    if (saving) return
    if (step === 'intro') {
      setStep(0)
      return
    }
    if (step === 'result') return
    if (step === 0 && personas.length === 0) return
    if (step === 1 && propertyTypes.length === 0) return
    if (step === 2 && !pace) return
    if (step !== 3) {
      setStep((step + 1) as Step)
      return
    }
    setSaving(true)
    try {
      await onAnswered?.({
        personas,
        personaOther: personaOther.trim() || null,
        propertyTypes,
        pace,
        paceOther: paceOther.trim() || null,
        missingFeature: missing.trim() || null,
      })
    } catch {
      toast.error('Réponses non enregistrées. Réessayez.')
      return
    } finally {
      setSaving(false)
    }
    if (embedded) {
      onOpenChange?.(false)
      return
    }
    setStep('result')
  }

  function goBack() {
    if (step === 'result') setStep(3)
    else if (step === 0) setStep('intro')
    else if (step !== 'intro') setStep((step - 1) as Step)
  }

  return (
    <div className={embedded ? undefined : 'min-h-screen flex flex-col bg-background text-foreground'}>
      <header className={embedded ? 'hidden' : 'border-b border-border bg-background sticky top-0 z-30'}>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 h-14 flex items-center justify-between gap-3">
          <Logo />
          <div className="flex items-center gap-2">
            <Badge variant="outline">Maquette</Badge>
            <Button variant="ghost" size="sm" onClick={restart}>Revoir le brief</Button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className={embedded ? 'hidden' : 'flex-1 max-w-[1280px] w-full mx-auto px-4 md:px-6 py-6'}>
        <div className="border border-border rounded-xl overflow-hidden bg-surface">
          <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-border bg-primary-soft">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-background text-primary">
              <Lock className="h-4 w-4" aria-hidden="true" />
            </span>
            <p className="min-w-0 flex-1 text-sm">
              <span className="font-mono font-semibold tabular-nums">{maskedLeft} biens</span> supplémentaires à {COMMUNE}.
              {ficheOpen ? ' Une adresse est ouverte, pour juger la donnée.' : ' 15 jours de l’abonnement, sans carte, après quatre questions.'}
            </p>
          </div>

          <div className="grid lg:grid-cols-[minmax(0,1.15fr)_minmax(280px,0.85fr)] min-h-[560px] relative">
            <div>
              <div className="hidden md:grid grid-cols-[minmax(0,1.4fr)_108px_42px_52px_72px] gap-3 items-center h-9 px-4 bg-background-2 text-xs font-semibold text-muted border-b border-border">
                <span>Adresse</span>
                <span className="text-right">Prix estimé</span>
                <span>DPE</span>
                <span className="text-right">Proba.</span>
                <span className="text-right">Reçu</span>
              </div>
              {VISIBLE_ROWS.map((row) => (
                <article key={row.address} className="grid grid-cols-[minmax(0,1fr)_auto_auto] md:grid-cols-[minmax(0,1.4fr)_108px_42px_52px_72px] gap-3 items-center min-h-[68px] px-4 border-b border-border">
                  <div className="min-w-0">
                    <div className="font-semibold tracking-tight truncate">{row.address}</div>
                    <div className="text-sm text-muted mt-0.5 truncate">{COMMUNE} · {row.meta}</div>
                  </div>
                  <div className="text-right font-mono font-semibold tabular-nums">{price.format(row.price)}</div>
                  <DpeGradeBadge grade={row.grade} size="sm" />
                  <div className="hidden md:block text-right font-mono font-semibold tabular-nums text-sm">{row.pct} %</div>
                  <div className="hidden md:block text-right font-mono tabular-nums text-xs text-muted">{row.when}</div>
                </article>
              ))}
              <div className="flex items-center gap-3 min-h-14 px-4 text-sm text-muted">
                <span className="grid h-8 w-8 place-items-center rounded-full bg-primary-soft text-primary">
                  <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                </span>
                <span>
                  <span className="font-mono font-semibold tabular-nums text-foreground">{maskedLeft}</span> biens masqués dans cette zone
                </span>
              </div>
            </div>

            <div className="relative hidden lg:block border-l border-border bg-background-2 min-h-[560px]" aria-hidden="true">
              <svg className="absolute inset-0 h-full w-full" viewBox="0 0 640 720" preserveAspectRatio="xMidYMid slice">
                <g stroke="var(--border)" strokeWidth="8" fill="none">
                  <path d="M30 90 H610 M30 210 H610 M30 340 H610 M30 470 H610 M30 600 H610" />
                  <path d="M80 20 V700 M200 20 V700 M330 20 V700 M470 20 V700 M590 20 V700" />
                </g>
                <circle cx="330" cy="360" r="150" fill="none" stroke="var(--primary)" strokeOpacity="0.45" strokeDasharray="4 6" />
              </svg>
              <p className="absolute left-4 bottom-4 text-xs font-semibold text-muted">{COMMUNE} · rayon 12 km</p>
              <Bead className="left-[42%] top-[38%] bg-dpe-e" label="Bien masqué, DPE E" onClick={reveal} />
              <Bead className="left-[63%] top-[28%] bg-dpe-f" label="Bien masqué, DPE F" onClick={reveal} />
              <Bead className="left-[55%] top-[62%] bg-dpe-d" label="Bien masqué, DPE D" onClick={reveal} />
              <Bead className="left-[28%] top-[52%] bg-dpe-g" label="Bien masqué, DPE G" onClick={reveal} />
            </div>

            {ficheOpen ? (
              <aside className="lg:absolute lg:top-3 lg:right-3 lg:bottom-3 lg:w-[360px] z-10 bg-background border-t lg:border border-border lg:rounded-xl lg:shadow-[0_16px_36px_-20px_oklch(0.28_0.02_55/0.35)] overflow-auto">
                <div className="flex items-start justify-between gap-3 px-4 pt-4">
                  <div>
                    <p className="text-sm text-muted">Fiche offerte, une seule par compte</p>
                    <h2 className="mt-1 text-xl font-semibold tracking-tight">{GIFT.address}</h2>
                    <p className="mt-1 text-sm text-muted">{GIFT.meta}</p>
                  </div>
                  <button type="button" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted hover:text-foreground" aria-label="Fermer la fiche" onClick={() => setFicheOpen(false)}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="px-4 pb-4 pt-4">
                  <div className="grid grid-cols-3 gap-3">
                    <Fact label="Prix estimé" value={price.format(GIFT.price)} />
                    <Fact label="Prix/m²" value={pricePerSquareMeter.format(Math.round(GIFT.price / GIFT.surface))} />
                    <Fact label="Probabilité" value={`${GIFT.pct} %`} />
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <DpeGradeBadge grade={GIFT.energy} size="sm" />
                    <DpeGradeBadge grade={GIFT.ges} size="sm" scale="ges" />
                    <span className="text-sm text-muted">Énergie · émissions</span>
                  </div>
                  <ul className="mt-3 text-sm">
                    <Signal>DPE réalisé il y a 11 mois</Signal>
                    <Signal>Logement vacant</Signal>
                    <Signal>Aucune annonce publiée</Signal>
                  </ul>
                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-sm">
                      <span className="font-mono font-semibold tabular-nums">{DETECTED - 1}</span> autres adresses dans ce secteur. {alerts}.
                    </p>
                    <Button className="mt-3" onClick={() => setConfirmOpen(true)}>Recevoir les adresses</Button>
                    <p className="mt-2 text-sm text-muted">Carte enregistrée. Rien n’est prélevé avant le {charge}.</p>
                  </div>
                </div>
              </aside>
            ) : null}
          </div>
        </div>
      </main>

      <Dialog open={briefOpen} onOpenChange={setBriefOpen}>
        <DialogContent
          hideClose
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          className="max-w-md gap-0 p-0"
        >
          <div className="px-6 pt-6 pr-12">
            {step === 'intro' ? null : (
              <p className="font-mono text-xs text-muted tabular-nums">{step === 'result' ? 'Votre zone' : `${step + 1} / 4`}</p>
            )}
            <DialogTitle className="mt-1 text-[1.35rem] leading-tight">
              {step === 'intro'
                ? 'Essai gratuit'
                : step === 'result'
                  ? COMMUNE
                  : step === 3
                    ? 'Quelle fonctionnalité vous manque-t-il dans votre quotidien ?'
                    : QUESTIONS[step].title}
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm leading-relaxed">
              {step === 'intro'
                ? 'Répondez aux questions. Vous avez alors accès à tout l’abonnement, pendant 15 jours. Aucune carte n’est demandée.'
                : step === 'result'
                  ? `${DETECTED} biens détectés, ${VISIBLE} visibles. Rayon 12 km.`
                  : step === 3
                    ? null
                    : QUESTIONS[step].help}
            </DialogDescription>
          </div>

          <div className="px-6 pt-5">
            {step === 'result' && personas.length > 0 ? (
              <div>
                <p className="text-sm leading-relaxed">{whoLine(personas, personaOther)}</p>
                <p className="mt-2 text-sm font-semibold leading-relaxed">
                  {lead ? MECHANISM[lead] : 'Les adresses sortent avant l’annonce, avec le prix estimé et le DPE.'}
                </p>
                {sought ? <p className="mt-3 text-sm text-muted">Biens cherchés : {sought}.</p> : null}
                {sought ? <p className="mt-3 text-sm text-muted">Biens cherchés : {sought}.</p> : null}
                {missing.trim() ? <p className="mt-3 text-sm text-muted">Il vous manque : {missing.trim()}.</p> : null}
                <dl className="mt-4 border-y border-border text-sm">
                  <OfferRow label="Essai" value={`${tierMeta.name}, ${TRIAL_DAYS} jours`} />
                  <OfferRow label="Alertes" value={alerts} />
                  <OfferRow label="Ensuite" value={`${formatTierPrice(tierMeta.priceCents)}/mois. Rien avant le ${charge}.`} />
                </dl>
              </div>
            ) : step === 'intro' ? (
              <ul className="text-sm">
                <Signal>Toutes les adresses du secteur, pas seulement 3</Signal>
                <Signal>Fiches, prix estimé, DPE</Signal>
                <Signal>Alertes à 8 h, pendant 15 jours</Signal>
              </ul>
            ) : step === 3 ? (
              <div>
                <Label htmlFor="missing-feature">Votre réponse</Label>
                <textarea
                  id="missing-feature"
                  value={missing}
                  onChange={(e) => setMissing(e.target.value)}
                  rows={4}
                  placeholder="Écrivez ici…"
                  className="mt-1.5 flex w-full rounded-md border border-input bg-surface px-3 py-2 text-sm text-foreground placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
            ) : step !== 'result' ? (
              <ChoiceList
                label={QUESTIONS[step].title}
                options={QUESTIONS[step].options}
                multiple={step === 0 || step === 1}
                value={step === 2 ? pace : null}
                values={step === 0 ? personas : step === 1 ? propertyTypes : []}
                onChange={(id) => {
                  if (step === 0) toggle(personas, id as Persona, setPersonas)
                  if (step === 1) toggle(propertyTypes, id as PropertyType, setPropertyTypes)
                  if (step === 2) setPace(id as Pace)
                }}
                other={step === 2 ? paceOther : personaOther}
                onOther={step === 2 ? setPaceOther : setPersonaOther}
                otherPlaceholder={QUESTIONS[step].otherPlaceholder}
              />
            ) : null}
          </div>

          <div className="mt-6 flex items-center justify-between gap-2 border-t border-border px-6 py-4">
            <Button variant="ghost" disabled={step === 'intro'} onClick={goBack}>Retour</Button>
            {step === 'result' ? (
              <Button onClick={reveal}>Voir une adresse</Button>
            ) : step === 'intro' ? (
              <Button onClick={continueBrief}>Commencer</Button>
            ) : (
              <Button
                onClick={continueBrief}
                disabled={saving || (step === 0 && personas.length === 0) || (step === 1 && propertyTypes.length === 0) || (step === 2 && !pace)}
              >
                Continuer
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogTitle>Deux lundis d’alertes</DialogTitle>
          <DialogDescription>{tierMeta.name}, {COMMUNE}. Rien n’est prélevé avant le {charge}.</DialogDescription>
          <dl className="border-y border-border text-sm">
            <OfferRow label="Alertes" value={alerts} />
            <OfferRow label="Volume" value={`${DETECTED} adresses dans le secteur`} />
            <OfferRow label="Ensuite" value={`${formatTierPrice(tierMeta.priceCents)}/mois, sans engagement`} />
          </dl>
          <div className="mt-6 flex flex-wrap justify-end gap-2">
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>Rester sur Découverte</Button>
            <Button onClick={() => { setConfirmOpen(false); toast.info('Maquette. Aucune carte n’est enregistrée.') }}>
              Enregistrer la carte et commencer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function ChoiceList({
  label,
  options,
  multiple = false,
  value,
  values = [],
  onChange,
  other,
  onOther,
  otherPlaceholder,
}: {
  label: string
  options: readonly { id: string; label: string; hint?: string }[]
  multiple?: boolean
  value: string | null
  values?: readonly string[]
  onChange: (id: string) => void
  other: string
  onOther: (value: string) => void
  otherPlaceholder: string
}) {
  return (
    <div role={multiple ? 'group' : 'radiogroup'} aria-label={label} className="flex flex-col gap-2">
      {options.map((option) => {
        const selected = multiple ? values.includes(option.id) : value === option.id
        return (
          <div key={option.id} className={`rounded-md border ${selected ? 'border-primary bg-primary-soft' : 'border-border bg-surface'}`}>
            <button
              type="button"
              role={multiple ? 'checkbox' : 'radio'}
              aria-checked={selected}
              onClick={() => onChange(option.id)}
              className="flex w-full items-center gap-3 px-3 py-3 text-left text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
            >
              {multiple ? (
                <span aria-hidden="true" className={`grid h-4 w-4 shrink-0 place-items-center rounded-[4px] border ${selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-background'}`}>
                  {selected ? (
                    <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 6.2 4.6 9 10 3" /></svg>
                  ) : null}
                </span>
              ) : null}
              <span className="flex-1">{option.label}</span>
            </button>
            {option.id === 'autre' && selected ? (
              <div className="px-3 pb-3">
                <label className="sr-only" htmlFor={`${label}-autre`}>Précisez</label>
                <input
                  id={`${label}-autre`}
                  value={other}
                  onChange={(e) => onOther(e.target.value)}
                  placeholder={otherPlaceholder}
                  className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground placeholder:text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

function OfferRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border py-2.5 last:border-b-0">
      <dt className="text-muted">{label}</dt>
      <dd className="text-right font-medium">{value}</dd>
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-t border-border pt-2">
      <span className="block text-xs text-muted">{label}</span>
      <b className="mt-0.5 block font-mono text-sm font-semibold tabular-nums">{value}</b>
    </div>
  )
}

function Signal({ children }: { children: string }) {
  return (
    <li className="flex gap-2 border-t border-border py-2">
      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
      {children}
    </li>
  )
}

function Bead({ className, label, onClick }: { className: string; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={`absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border border-background shadow-[0_0_0_5px_color-mix(in_oklch,currentColor_22%,transparent)] ${className}`}
    />
  )
}
